<template>
  <SCADropdown
    v-model:is-open="open"
    :padding-upset="paddingUpset"
    placement="bottom-start"
    :offset-y="FILTER_DROPDOWN_OFFSET_PX"
    trigger="click"
    no-mobile-margin
    :style="active ? {} : { width: '100%' }"
    @positioned="emit('positioned')"
    @closed="emit('closed')"
  >
    <template #trigger="{ target }">
      <div :class="[$style.trigger, (open || hovered) && $style.triggerHighlighted]">
        <SCAButton
          :popovertarget="target"
          type="bare"
          size="xsmall"
          intent="none"
          appearance="secondary"
          :class="$style.button"
          icon-left="phosphor/funnel"
          :style="{ opacity: triggerVisible ? 1 : 0 }"
          :test-id="`${testIdPrefix}-trigger`"
        />
      </div>
    </template>
    <template #default>
      <slot />
    </template>
  </SCADropdown>
  <!-- The dropdown wrapper is positioned, so the area is a sibling to anchor on the row (padding included). -->
  <div ref="hitAreaRef" :class="$style.hitArea" @click="open = true"></div>
  <div :class="$style.value" @click="open = true">
    <span ref="summaryRef" :class="$style.summary" :test-id="`${testIdPrefix}-summary`">
      {{ summary }}
    </span>
    <SCATooltip v-if="isTruncated" :target="summaryRef" :text="summary" />
  </div>
  <SCASvgIcon
    :class="$style.clear"
    :style="{ opacity: clearVisible ? 1 : 0, pointerEvents: clearVisible ? 'auto' : 'none' }"
    size="12px"
    icon="phosphor/x"
    :test-id="`${testIdPrefix}-clear`"
    @click="emit('clear')"
  />
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import { findDropdownAnchor } from "../Dropdown/dropdownAnchor";
import SCADropdown from "../Dropdown/SCADropdown.vue";
import SCAButton from "../SCAButton.vue";
import SCASvgIcon from "../SCASvgIcon.vue";
import SCATooltip from "../SCATooltip.vue";
import { useIsTruncated } from "../useIsTruncated";
import { FILTER_DROPDOWN_OFFSET_PX } from "./constants";
import { useCloseWhenAnchorClipped } from "./useCloseWhenAnchorClipped";

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  summary: string;
  active: boolean;
  hovered: boolean;
  testIdPrefix: string;
  paddingUpset?: boolean;
}>();

const emit = defineEmits<{
  positioned: [];
  closed: [];
  clear: [];
}>();

const open = defineModel<boolean>("open", { required: true });

const hitAreaRef = ref<HTMLElement | null>(null);
useCloseWhenAnchorClipped({
  open,
  anchor: () => findDropdownAnchor(hitAreaRef.value),
  close: () => (open.value = false),
});

const summaryRef = ref<HTMLElement | null>(null);
const isTruncated = useIsTruncated(summaryRef, () => props.summary);

const triggerVisible = computed(() => props.hovered || props.active || open.value);
const clearVisible = computed(() => props.active && (props.hovered || open.value));
</script>

<style module lang="scss">
@use "../../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;
@use "../../styles/icon.scss" as IconMixins;

.trigger {
  display: flex;
  width: 100%;
}

.trigger :global(svg) {
  @include IconMixins.icon-color(var(--color-content-disabled));
  @include IconMixins.icon-clickable;
}

.trigger.triggerHighlighted :global(svg) {
  @include IconMixins.icon-color(var(--color-blue-p1));
}

.button {
  background-color: var(--color-background-default);
  color: inherit;
  padding-left: 0;
  padding-right: 0;
}

.button svg {
  width: 16px;
  height: 16px;
}

.clear {
  position: absolute;
  right: 0;
  background-color: var(--color-background-default);
  color: var(--color-content-primary);
  @include IconMixins.icon-clickable;
}

.hitArea {
  position: absolute;
  inset: 0 calc(-1 * var(--space-component-padding-small));
}

.value {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: var(--space-component-gap-xSmall);
  padding-right: var(--space-component-padding-large);
  white-space: nowrap;
  @include FigmaTypoMixins.typo-body-medium;
  color: var(--color-content-secondary);
}

.summary {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
